import type { Dict } from "../types";

export const payments: Dict = {
  en: {

    // ── Module « Payments » (Stripe Connect) ────────────────────────────────
    "Payments.title": "Payments",
    "Payments.collect.title": "To collect",
    "Payments.collect.subtitle": "Unpaid invoices. Send a link and your client pays by card.",
    "Payments.collect.empty": "Nothing left to collect.",
    "Payments.collect.error": "This list cannot be displayed right now. Please try again in a moment.",
    "Payments.collect.noCustomer": "No client on file",
    "Payments.subtitle": "Take card payments from your own clients, with no Flowcut commission.",
    "Payments.loadError": "Online payment settings could not be loaded. Please try again in a moment.",
    "Payments.comingSoon.title": "Online payments are coming soon",
    "Payments.comingSoon.body": "We are finishing the last checks with Stripe before opening activation. You will be able to turn the module on right here as soon as it is ready.",
    "Payments.cta.title": "Online payments: free",
    "Payments.cta.body": "Take card payments from your clients: deposits at booking time, and invoices paid remotely.",
    "Payments.cta.fees": "No Flowcut commission. You only pay the standard Stripe fees (2.9% + $0.30).",
    "Payments.cta.oneClick": "Turn it on in one click.",
    "Payments.toggle.label": "Enable online payments",
    "Payments.toggle.disableWarning": "Turning this off only affects what comes next. Deposits already paid stay valid, checkouts already under way are honoured, and asking for a deposit at booking turns off together with online payments.",
    "Payments.activate.success": "Online payments are enabled.",
    "Payments.activate.error": "Online payments could not be enabled.",
    "Payments.deactivate.success": "Online payments are disabled.",
    "Payments.deactivate.error": "Online payments could not be disabled.",
    "Payments.tiles.module": "Module",
    "Payments.tiles.account": "Stripe account",
    "Payments.tiles.received": "Payments received",
    // ── Lot 2 : la bande d'état (C5) ────────────────────────────────────────
    "Payments.strip.accountActive": "Stripe account active",
    "Payments.strip.okBody": "Charges and payouts are on, online payments are accepted.",
    "Payments.strip.paused": "Online payments paused",
    "Payments.strip.pausedBody": "Your clients can no longer pay by link.",
    "Payments.strip.showDetails": "Details",
    "Payments.strip.hideDetails": "Hide details",
    "Payments.settings.activeHint": "The Payments module is now in your menu: your Stripe account and your transactions live there.",
    "Payments.inactive.title": "Online payments are turned off",
    "Payments.inactive.body": "Turn them back on in Salon Information, Payments tab.",
    "Payments.managedInSettings.title": "Managed in Salon Information",
    "Payments.managedInSettings.body": "Turning online payments on or off happens in Salon Information, Payments tab.",
    "Payments.terms.title": "Online payment terms",
    "Payments.terms.intro": "Read these three points before turning online payments on. Accepting them transfers real responsibilities to your salon.",
    "Payments.terms.merchant.title": "Your salon becomes the official merchant",
    "Payments.terms.merchant.body": "Payments go straight to your Stripe account, so your salon, not Flowcut, is the merchant of record for your client. Disputes, refunds and taxes are your responsibility.",
    "Payments.terms.fees.title": "Fees",
    "Payments.terms.fees.body": "Flowcut takes no commission. Transaction fees are Stripe’s, and they are taken directly from your salon’s payments. These fees can change: Stripe’s official pricing is what applies.",
    "Payments.terms.fees.link": "See Stripe pricing",
    "Payments.terms.payouts.title": "Payout timing",
    "Payments.terms.payouts.body": "Your very first payout takes 7 to 14 days, depending on your risk profile. After that, payouts reach your bank within 3 business days.",
    "Payments.terms.checkbox": "I understand and accept these terms.",
    "Payments.terms.mustAccept": "Check the box to accept the terms before activating.",
    "Payments.terms.accept": "Accept and enable",
    "Payments.terms.acceptedOn": "Terms accepted on",
    "Payments.account.title": "Account and connection",
    "Payments.account.subtitle": "Your Stripe account collects the money and pays it out to your bank.",
    "Payments.account.badge.none": "Not connected",
    "Payments.account.badge.onboarding": "In progress",
    "Payments.account.badge.active": "Active",
    "Payments.account.badge.restricted": "Restricted",
    "Payments.account.charges": "Charges",
    "Payments.account.payouts": "Payouts",
    "Payments.account.detailsSubmitted": "Details submitted",
    "Payments.account.state.on": "Enabled",
    "Payments.account.state.off": "Not yet",
    "Payments.account.firstPayout.title": "Your first payout takes longer",
    "Payments.account.firstPayout.body": "Stripe holds the very first payout of a new account for 7 to 14 days, depending on your risk profile. After that, money reaches your bank within 3 business days. This is normal, not a bug.",
    "Payments.account.connect": "Connect my account",
    "Payments.account.resume": "Finish setting up my account",
    "Payments.account.dashboard": "Open my Stripe dashboard",
    "Payments.account.refreshStatus": "Refresh status",
    "Payments.account.refreshSuccess": "Status refreshed.",
    "Payments.account.refreshError": "The status could not be refreshed.",
    "Payments.account.connectError": "The Stripe setup link could not be opened.",
    "Payments.account.dashboardError": "Your Stripe dashboard could not be opened.",
    "Payments.account.ownerOnly": "Only the salon owner can change these settings.",
    "Payments.deposits.alignConfirmTitle": "Realign every service?",
    "Payments.deposits.alignAllOne": "Realign 1 service on the salon policy",
    "Payments.deposits.alignAllMany": "Realign {n} services on the salon policy",
    "Payments.deposits.alignConfirmBodyOne": "1 service currently follows its own deposit rule. It will go back to the salon policy, and its setting is lost.",
    "Payments.deposits.alignConfirmBodyMany": "{n} services currently follow their own deposit rule. They will go back to the salon policy, and those settings are lost.",
    "Payments.deposits.alignHiddenNote": "Services that cannot be booked online are counted too, even though they are not listed above.",
    "Payments.deposits.ownerOnly": "Only the owner can change the deposit settings.",
    "Payments.deposits.alignCancel": "Cancel",
    "Payments.deposits.alignConfirm": "Realign",
    "Payments.deposits.alignDone": "Every service now follows the salon policy.",
    "Payments.deposits.alignFailed": "Services could not be realigned.",
    "Payments.deposits.alignAlreadyRunning": "A realignment is already running.",
    "Payments.deposits.alignTimeout": "Realigning did not answer in time. We do not know yet whether it went through: reload the page to see the real state.",
    "Payments.deposits.title": "Deposits by service",
    "Payments.deposits.body": "What each service will ask for at booking time. Read only: a deposit is edited on the service's own sheet, so there is a single place to change it.",
    "Payments.deposits.empty": "No service to show yet.",
    "Payments.deposits.loadError": "Deposits could not be loaded. Nothing here says what your services ask for right now.",
    "Payments.deposits.overridden": "Own rule",
    "Payments.deposits.none": "No deposit",
    "Payments.deposits.rangeNoneTo": "{min} to {max} depending on the employee",
    "Payments.deposits.range": "{min} to {max}, depending on the team member",
    // ── Lot 2 : le bandeau de politique (C4) ────────────────────────────────
    "Payments.deposits.policyPercentage": "Salon policy: {value}% of the price at booking.",
    "Payments.deposits.policyFixed": "Salon policy: {amount} at booking.",
    "Payments.deposits.policyFlat": "Salon policy: {amount} for the whole appointment.",
    "Payments.deposits.policyThreshold": "Only for bookings of {amount} or more.",
    "Payments.deposits.policyAllFollow": "Every service follows the salon policy.",
    "Payments.deposits.policyOverriddenOne": "1 service follows its own rule.",
    "Payments.deposits.policyOverriddenMany": "{n} services follow their own rule.",
    "Payments.deposits.soloNote": "Amounts shown are for a service booked on its own. When several services are booked together, the minimum and the cap apply to the whole basket.",
    "Payments.deposits.editOnService": "Edit on the service sheet",
    "Payments.depositPolicy.title": "Deposits at booking",
    "Payments.depositPolicy.body": "Ask your clients for a deposit when they book online. The amount is worked out from the price of the services booked.",
    "Payments.depositPolicy.enable": "Ask for a deposit at booking",
    "Payments.depositPolicy.needsPayments": "Turn online payments on first: a deposit is collected by card, so it cannot work without them.",
    "Payments.depositPolicy.typeLabel": "How the deposit is worked out",
    "Payments.depositPolicy.typePercentage": "A share of the total",
    "Payments.depositPolicy.typeFixed": "A fixed amount",
    "Payments.depositPolicy.valuePercentage": "Share of the total (%)",
    "Payments.depositPolicy.valueFixed": "Amount ($)",
    "Payments.depositPolicy.floorNote": "Below {floor} no deposit is asked, and none is asked either when the minimum would exceed half the price of the booking.",
    "Payments.depositPolicy.refundable": "Give the deposit back when a client cancels in time",
    "Payments.depositPolicy.windowLabel": "Cancel at least this many hours ahead",
    "Payments.depositPolicy.lastminuteLabel": "A booking made inside that window",
    "Payments.depositPolicy.lastminuteNonRefundable": "Keep the deposit",
    "Payments.depositPolicy.lastminuteRefundable": "Give the deposit back anyway",
    "Payments.depositPolicy.lastminuteNoDeposit": "Ask for no deposit at all",
    "Payments.depositPolicy.save": "Save the deposit settings",
    "Payments.depositPolicy.saved": "Deposit settings saved.",
    "Payments.depositPolicy.saveFailed": "Deposit settings could not be saved.",
    "Payments.depositPolicy.ownerOnly": "Only the owner can change the deposit settings.",
    "Payments.depositPolicy.invalidValue": "Enter an amount greater than zero.",
    "Payments.depositPolicy.percentageTooHigh": "A percentage cannot be greater than 100.",
    "Payments.depositPolicy.invalidWindow": "Enter a whole number of hours, zero or more.",
    "Payments.depositPolicy.depositFloorRefusal": "A fixed deposit cannot be lower than {floor}.",
    "Payments.depositPolicy.windowRefusal": "The cancellation window cannot be more than {max} hours.",
    "Payments.depositPolicy.invalidSetting": "This setting is not valid. Please check the entered values.",

    // ── Mode « un montant pour tout le rendez-vous » et seuil réglable ──────
    "Payments.depositPolicy.typeFlat": "One amount for the whole booking",
    "Payments.depositPolicy.valueFlat": "Amount for the booking ($)",
    "Payments.depositPolicy.minBasketLabel": "Only when the booking is over ($)",
    "Payments.depositPolicy.minBasketHelp": "Below this total, no deposit is asked.",
    "Payments.depositPolicy.invalidMinBasket": "Enter an amount of zero or more.",
    "Payments.depositPolicy.flatRatioWarning": "With this threshold, a client whose booking is just above it would pay more than half the price up front.",
    "Payments.depositPolicy.lastminuteFlaggedNote": "This exemption never applies to a flagged client.",

    // ── Carte « Clients signalés » ──────────────────────────────────────────
    "Payments.flaggedDeposit.title": "Flagged clients",
    "Payments.flaggedDeposit.body": "Ask a deposit from the clients you have flagged, even when you ask none from everyone else.",
    "Payments.flaggedDeposit.enable": "Ask flagged clients for a deposit",
    "Payments.flaggedDeposit.valueLabel": "Amount ($)",
    "Payments.flaggedDeposit.minBasketLabel": "From ($)",
    "Payments.flaggedDeposit.discretionNote": "The client never sees any mention of this status.",
    "Payments.flaggedDeposit.whereNote": "You set the status on the client file, Profile tab, Client type field.",
    "Payments.flaggedDeposit.onlineOnlyNote": "Online bookings only. Nothing changes at the counter or on the phone line.",
    "Payments.flaggedDeposit.needsPayments": "Turn online payments on first: a deposit is collected by card, so it cannot work without them.",
    "Payments.flaggedDeposit.invalidValue": "Enter an amount greater than zero.",
    "Payments.flaggedDeposit.invalidMinBasket": "Enter an amount of zero or more.",
    "Payments.flaggedDeposit.save": "Save the flagged client settings",
    "Payments.flaggedDeposit.saved": "Flagged client settings saved.",
    "Payments.flaggedDeposit.saveFailed": "Flagged client settings could not be saved.",
    "Payments.flaggedDeposit.ownerOnly": "Only the owner can change these settings.",

    "Payments.charges.title": "Accept online payments",
    "Payments.charges.onBody": "Your clients can pay their invoices by card. Turn this off to stop accepting payments without disconnecting your Stripe account.",
    "Payments.charges.offBody": "Online payments are paused. Your Stripe account stays connected and you keep access to the money already collected.",
    "Payments.charges.blockedByStripe": "Stripe is not accepting payments on your account yet. Finish your verification so the money can come in.",
    "Payments.charges.ownerOnly": "Only the salon owner can change this setting.",
    "Payments.charges.resumed": "Online payments resumed.",
    "Payments.charges.pausedToast": "Online payments paused.",
    "Payments.charges.error": "This setting could not be saved.",
    "Payments.onboardingCta.title": "Finish connecting your Stripe account",
    "Payments.onboardingCta.body": "Deposits and online invoice payments stay off until Stripe confirms your account can take payments.",
    "Payments.onboardingCta.action": "Continue setup",
    "Payments.transactions.title": "Transactions",
    "Payments.transactions.subtitle": "Every deposit and invoice paid online, with its Stripe status.",
    "Payments.transactions.empty": "No online payment yet.",
    "Payments.transactions.loadError": "Transactions could not be loaded.",
    "Payments.transactions.col.date": "Date",
    "Payments.transactions.col.kind": "Type",
    "Payments.transactions.col.amount": "Amount",
    "Payments.transactions.col.status": "Status",
    "Payments.transactions.col.card": "Card",
    "Payments.transactions.col.action": "Action",
    "Payments.transactions.kind.deposit": "Deposit",
    "Payments.transactions.kind.invoice": "Invoice",
    "Payments.transactions.status.pending": "Pending",
    "Payments.transactions.status.paid": "Paid",
    "Payments.transactions.status.failed": "Failed",
    "Payments.transactions.status.refund_pending": "Refund pending",
    "Payments.transactions.status.partially_refunded": "Partially refunded",
    "Payments.transactions.status.refunded": "Refunded",
    "Payments.transactions.status.expired": "Expired",
    "Payments.transactions.linkCreated": "Link created",
    "Payments.transactions.refundedAmount": "Refunded:",
    "Payments.transactions.refundableRemaining": "Refundable balance:",
    "Payments.transactions.refundSuccess": "Refund sent to Stripe.",
    "Payments.transactions.refundUnconfirmed": "This refund is not confirmed. We are checking it with Stripe: no other refund will go out on this transaction until that check is done, within two minutes.",
    "Payments.reauth.settings.title": "Confirm your identity",
    "Payments.reauth.settings.description": "Payment settings move real money. Confirm it is really you before changing them.",
    "Payments.reauth.refund.title": "Confirm this refund",
    "Payments.reauth.refund.description": "A refund sends money back to your client. Confirm it is really you.",

    // ── Refonte du 10-09 : onglets, pagination, recherche ───────────────────
    "Payments.tabs.collect": "To collect",
    "Payments.tabs.transactions": "Transactions",
    "Payments.tabs.deposits": "Deposits by service",
    "Payments.list.searchPlaceholder": "Search",
    "Payments.list.noResults": "No result for \"{query}\".",
    "Payments.list.noResultsFiltered": "No result for these filters.",
    "Payments.collect.totalDue": "{amount} to recover",
    "Payments.collect.countLine": "{count} unpaid invoices, {late} of them over {critical} days old",
    "Payments.collect.countLineOne": "1 unpaid invoice, {late} over {critical} days old",
    "Payments.collect.sort.label": "Sort",
    "Payments.collect.sort.oldest": "Oldest first",
    "Payments.collect.sort.newest": "Newest first",
    "Payments.collect.sort.amount": "Largest amount first",
    "Payments.collect.col.customer": "Client",
    "Payments.collect.col.invoice": "Invoice",
    "Payments.collect.col.issued": "Issued",
    "Payments.collect.col.age": "Age",
    "Payments.collect.col.due": "Due",
    "Payments.collect.col.action": "Action",
    "Payments.age.days": "{count} days",
    "Payments.age.oneDay": "1 day",
    "Payments.age.today": "Today",
    "Payments.age.unknown": "Unknown",
    "Payments.transactions.totals.collected": "Collected",
    "Payments.transactions.totals.refunded": "Refunded",
    "Payments.transactions.totals.net": "Net",
    "Payments.transactions.totals.succeeded": "{succeeded} successful transactions out of {attempted} attempts",
    "Payments.transactions.filter.status": "Status",
    "Payments.transactions.filter.kind": "Type",
    "Payments.transactions.filter.all": "All",
    "Payments.transactions.menu.label": "Actions",
    "Payments.transactions.menu.refundBalance": "Refund the balance, {amount}",
    "Payments.transactions.menu.refundAmount": "Refund an amount",
    "Payments.refundDialog.title": "Refund an amount",
    "Payments.refundDialog.body": "Up to {max} can still be refunded on this transaction.",
    "Payments.refundDialog.amountLabel": "Amount to refund",
    "Payments.refundDialog.confirm": "Refund",
    "Payments.refundDialog.cancel": "Cancel",
    "Payments.refundDialog.error.invalid_amount": "Enter a valid amount above zero, with at most two decimals.",
    "Payments.refundDialog.error.exceeds_refundable": "This amount is higher than what can still be refunded on this transaction.",
    "Payments.refundDialog.error.exceeds_invoice_collected": "This amount is higher than what this invoice still holds: a credit note or another refund has already returned part of its money.",
    "Payments.refundDialog.error.exceeds_invoice_collected_max": "Maximum possible: {max}.",
    "Payments.refundDialog.error.daily_cap_reached": "Your salon has reached its daily refund limit. Try again tomorrow.",
    "Payments.refundDialog.error.unknown": "The refund could not be completed.",
    "Payments.refundDialog.error.nothing_refundable": "There is nothing left to refund on this transaction.",
    "Payments.refundDialog.error.not_found": "This transaction could not be found. Refresh the page.",
    "Payments.refundDialog.error.refund_in_progress": "A refund is already being checked on this transaction. Try again in two minutes.",
    "Payments.refundDialog.error.invoice_refund_in_progress": "A refund is already being checked on another payment of this invoice. Try again in two minutes.",
    "Payments.refundDialog.error.daily_cap_unreadable": "The daily refund limit could not be checked, so nothing was refunded. Try again in a moment.",
    "Payments.refundDialog.error.transaction_unreadable": "This transaction could not be read, so nothing was refunded. Try again in a moment.",
    "Payments.refundDialog.error.stripe_refused": "Stripe declined this refund, and no money was moved. Check the amount, then try again.",
    "Payments.refundDialog.error.deposit_not_refundable_here": "A deposit cannot be refunded here: it follows the appointment cancellation policy.",
    "Payments.refundDialog.error.access_check_unavailable": "Access could not be checked right now. Please try again in a moment.",
    "Payments.refundDialog.error.access_denied": "You do not have permission to refund this payment.",
    "Payments.refundDialog.error.reauth_required": "For your security, please confirm your identity again, then try again.",
    "Payments.deposits.col.service": "Service",
    "Payments.deposits.col.price": "Price",
    "Payments.deposits.col.rule": "Rule",
    "Payments.deposits.col.deposit": "Deposit at booking",
    "Payments.deposits.ruleSalon": "Salon policy",

    // ── Lot 2 : l'état du lien de paiement (C2) ─────────────────────────────
    "Payments.collect.col.link": "Payment link",
    "Payments.collect.link.never": "Never sent",
    "Payments.collect.link.sent": "Sent on {date}",
    "Payments.collect.link.sentDetail": "by {channel}, expires on {date}",
    "Payments.collect.link.expiresOn": "expires on {date}",
    "Payments.collect.link.expired": "Expired on {date}",
    "Payments.collect.link.expiredNoDate": "Expired",
    "Payments.collect.link.revoked": "Cancelled on {date}",
    "Payments.collect.link.revokedNoDate": "Cancelled",
    "Payments.collect.link.channel.email": "email",
    "Payments.collect.link.channel.sms": "text message",
    "Payments.collect.link.channel.qr": "QR code",
    "Payments.collect.action.send": "Send",
    "Payments.collect.action.remind": "Send a reminder",
    "Payments.collect.action.resend": "Send again",

    // ── Lot 2 : la cliente et le rattachement (C1) ──────────────────────────
    "Payments.transactions.col.customer": "Client",
    "Payments.transactions.col.attached": "Linked to",
    "Payments.transactions.attachedInvoice": "Invoice {number}",
    "Payments.transactions.attachedDeposit": "Deposit, appointment on {date}",

    // ── Lot 2 : l'export (C3) ───────────────────────────────────────────────
    "Payments.export.one": "Export {count} row",
    "Payments.export.many": "Export {count} rows",
    "Payments.csv.ageDays": "Age in days",

    // ── Lot 2 : période, totaux filtrés (C4), tuile des totaux (m9) ─────────
    "Payments.transactions.totals.countLabel": "Transactions",
    "Payments.transactions.filter.period": "Period",
    "Payments.transactions.period.all": "All time",
    "Payments.transactions.period.thisMonth": "This month",
    "Payments.transactions.period.lastMonth": "Last month",
    "Payments.transactions.period.last30": "Last 30 days",
    "Payments.transactions.period.thisYear": "This year",
    "Payments.list.forTheseFilters": "Figures for these filters",

    // ── Lot 2 : le filtre d'âge (C4) ────────────────────────────────────────
    "Payments.collect.ageFilter.label": "Age",
    "Payments.collect.ageFilter.all": "All ages",
    "Payments.collect.ageFilter.under": "Under {max} days",
    "Payments.collect.ageFilter.between": "{min} to {max} days",
    "Payments.collect.ageFilter.atLeast": "{min} days or more",

    // ── Restes de l'audit multi-emplacements, problème 8 ────────────────────
    "Payments.brandWideSettingNote": "This setting applies to all your locations.",
  },
  fr: {

    // ── Module « Paiements » (Stripe Connect) ───────────────────────────────
    "Payments.title": "Paiements",
    "Payments.collect.title": "À encaisser",
    "Payments.collect.subtitle": "Vos factures impayées. Envoyez un lien, votre client règle par carte.",
    "Payments.collect.empty": "Il ne reste rien à encaisser.",
    "Payments.collect.error": "Cette liste ne peut pas s’afficher pour le moment. Réessayez dans un instant.",
    "Payments.collect.noCustomer": "Aucun client au dossier",
    "Payments.subtitle": "Encaissez vos propres clients par carte, sans aucune commission Flowcut.",
    "Payments.loadError": "Impossible de charger les réglages de paiement en ligne. Réessayez dans un instant.",
    "Payments.comingSoon.title": "Les paiements en ligne arrivent bientôt",
    "Payments.comingSoon.body": "Nous terminons les derniers essais avec Stripe avant d'ouvrir l'activation. Vous pourrez activer le module ici même dès que ce sera prêt.",
    "Payments.cta.title": "Paiements en ligne : gratuit",
    "Payments.cta.body": "Encaissez vos clients par carte : acomptes à la réservation, et factures payées à distance.",
    "Payments.cta.fees": "Aucune commission Flowcut. Vous ne payez que les frais Stripe standards (2,9 % + 0,30 $).",
    "Payments.cta.oneClick": "Activable en un clic.",
    "Payments.toggle.label": "Activer les paiements en ligne",
    "Payments.toggle.disableWarning": "La désactivation n'agit que sur la suite. Les acomptes déjà payés restent valides, les paiements déjà en cours sont honorés, et la demande d'acompte à la réservation s'éteint en même temps que les paiements en ligne.",
    "Payments.activate.success": "Les paiements en ligne sont activés.",
    "Payments.activate.error": "Impossible d'activer les paiements en ligne.",
    "Payments.deactivate.success": "Les paiements en ligne sont désactivés.",
    "Payments.deactivate.error": "Impossible de désactiver les paiements en ligne.",
    "Payments.tiles.module": "Module",
    "Payments.tiles.account": "Compte Stripe",
    "Payments.tiles.received": "Paiements reçus",
    // ── Lot 2 : la bande d'état (C5) ────────────────────────────────────────
    "Payments.strip.accountActive": "Compte Stripe actif",
    "Payments.strip.okBody": "Encaissement et virements actifs, paiements en ligne acceptés.",
    "Payments.strip.paused": "Paiements en ligne en pause",
    "Payments.strip.pausedBody": "Vos clients ne peuvent plus payer par lien.",
    "Payments.strip.showDetails": "Détails",
    "Payments.strip.hideDetails": "Masquer les détails",
    "Payments.settings.activeHint": "Le module « Paiements » est maintenant dans votre menu : votre compte Stripe et vos transactions s'y trouvent.",
    "Payments.inactive.title": "Les paiements en ligne sont désactivés",
    "Payments.inactive.body": "Réactivez-les dans « Informations du salon », onglet « Paiements ».",
    "Payments.managedInSettings.title": "Géré dans « Informations du salon »",
    "Payments.managedInSettings.body": "L'activation et la désactivation des paiements en ligne se font dans « Informations du salon », onglet « Paiements ».",
    "Payments.terms.title": "Conditions des paiements en ligne",
    "Payments.terms.intro": "Lisez ces trois points avant d'activer les paiements en ligne. Les accepter transfère de vraies responsabilités à votre salon.",
    "Payments.terms.merchant.title": "Votre salon devient le commerçant officiel",
    "Payments.terms.merchant.body": "Les paiements vont directement sur votre compte Stripe : c'est donc votre salon, et non Flowcut, qui est le commerçant officiel vis-à-vis de votre client. Les litiges, les remboursements et la fiscalité sont à votre charge.",
    "Payments.terms.fees.title": "Frais",
    "Payments.terms.fees.body": "Flowcut ne prend aucune commission. Les frais de transaction sont ceux de Stripe et sont prélevés directement sur les encaissements de votre salon. Ces frais peuvent changer : la tarification officielle de Stripe fait foi.",
    "Payments.terms.fees.link": "Voir la tarification de Stripe",
    "Payments.terms.payouts.title": "Délai des virements",
    "Payments.terms.payouts.body": "Votre tout premier virement prend de 7 à 14 jours, selon votre profil de risque. Ensuite, les virements arrivent en 3 jours ouvrables.",
    "Payments.terms.checkbox": "Je comprends et j'accepte les conditions.",
    "Payments.terms.mustAccept": "Cochez la case d'acceptation avant d'activer.",
    "Payments.terms.accept": "Accepter et activer",
    "Payments.terms.acceptedOn": "Conditions acceptées le",
    "Payments.account.title": "Compte et connexion",
    "Payments.account.subtitle": "Votre compte Stripe encaisse l'argent et le vire sur votre compte bancaire.",
    "Payments.account.badge.none": "Non connecté",
    "Payments.account.badge.onboarding": "En cours",
    "Payments.account.badge.active": "Actif",
    "Payments.account.badge.restricted": "Restreint",
    "Payments.account.charges": "Encaissement",
    "Payments.account.payouts": "Virements",
    "Payments.account.detailsSubmitted": "Dossier transmis",
    "Payments.account.state.on": "Actif",
    "Payments.account.state.off": "Pas encore",
    "Payments.account.firstPayout.title": "Votre premier virement est plus long",
    "Payments.account.firstPayout.body": "Stripe retient le tout premier virement d'un nouveau compte de 7 à 14 jours, selon votre profil de risque. Ensuite, l'argent arrive sur votre compte bancaire en 3 jours ouvrables. C'est normal, ce n'est pas un bogue.",
    "Payments.account.connect": "Connecter mon compte",
    "Payments.account.resume": "Terminer la configuration de mon compte",
    "Payments.account.dashboard": "Ouvrir mon tableau de bord Stripe",
    "Payments.account.refreshStatus": "Actualiser le statut",
    "Payments.account.refreshSuccess": "Statut actualisé.",
    "Payments.account.refreshError": "Impossible d'actualiser le statut.",
    "Payments.account.connectError": "Impossible d'ouvrir le lien de configuration Stripe.",
    "Payments.account.dashboardError": "Impossible d'ouvrir votre tableau de bord Stripe.",
    "Payments.account.ownerOnly": "Seul le propriétaire du salon peut modifier ces réglages.",
    "Payments.deposits.alignConfirmTitle": "Réaligner tous les services ?",
    "Payments.deposits.alignAllOne": "Réaligner 1 service sur la politique du salon",
    "Payments.deposits.alignAllMany": "Réaligner {n} services sur la politique du salon",
    "Payments.deposits.alignConfirmBodyOne": "1 service suit aujourd'hui sa propre règle d'acompte. Il repassera sous la politique du salon, et son réglage est perdu.",
    "Payments.deposits.alignConfirmBodyMany": "{n} services suivent aujourd'hui leur propre règle d'acompte. Ils repasseront sous la politique du salon, et ces réglages sont perdus.",
    "Payments.deposits.alignHiddenNote": "Les prestations non réservables en ligne sont comptées elles aussi, même si elles ne figurent pas dans la liste ci-dessus.",
    "Payments.deposits.ownerOnly": "Seul le propriétaire peut modifier les réglages d'acompte.",
    "Payments.deposits.alignCancel": "Annuler",
    "Payments.deposits.alignConfirm": "Réaligner",
    "Payments.deposits.alignDone": "Tous les services suivent désormais la politique du salon.",
    "Payments.deposits.alignFailed": "Les services n'ont pas pu être réalignés.",
    "Payments.deposits.alignAlreadyRunning": "Un réalignement est déjà en cours.",
    "Payments.deposits.alignTimeout": "Le réalignement n'a pas répondu à temps. Nous ne savons pas encore s'il a abouti : rechargez la page pour voir l'état réel.",
    "Payments.deposits.title": "Acomptes par service",
    "Payments.deposits.body": "Ce que chaque prestation demandera à la réservation. En lecture seule : un acompte se modifie sur la fiche du service, pour qu'il n'y ait qu'un seul endroit où le changer.",
    "Payments.deposits.empty": "Aucun service à afficher pour le moment.",
    "Payments.deposits.loadError": "Les acomptes n'ont pas pu être chargés. Rien ici ne dit ce que vos services demandent pour le moment.",
    "Payments.deposits.overridden": "Règle propre",
    "Payments.deposits.none": "Aucun acompte",
    "Payments.deposits.rangeNoneTo": "de {min} à {max} selon l'employé",
    "Payments.deposits.range": "{min} à {max}, selon l'employé",
    // ── Lot 2 : le bandeau de politique (C4) ────────────────────────────────
    "Payments.deposits.policyPercentage": "Politique du salon : {value} % du prix à la réservation.",
    "Payments.deposits.policyFixed": "Politique du salon : {amount} à la réservation.",
    "Payments.deposits.policyFlat": "Politique du salon : {amount} pour tout le rendez-vous.",
    "Payments.deposits.policyThreshold": "Seulement pour un rendez-vous de {amount} ou plus.",
    "Payments.deposits.policyAllFollow": "Tous les services suivent la politique du salon.",
    "Payments.deposits.policyOverriddenOne": "1 service suit sa propre règle.",
    "Payments.deposits.policyOverriddenMany": "{n} services suivent leur propre règle.",
    "Payments.deposits.soloNote": "Les montants affichés valent pour une prestation réservée seule. Quand plusieurs services sont réservés ensemble, le minimum et le plafond s'appliquent à l'ensemble du panier.",
    "Payments.deposits.editOnService": "Modifier sur la fiche du service",
    "Payments.depositPolicy.title": "Acomptes à la réservation",
    "Payments.depositPolicy.body": "Demandez un acompte à vos clients quand ils réservent en ligne. Le montant se calcule sur le prix des prestations réservées.",
    "Payments.depositPolicy.enable": "Demander un acompte à la réservation",
    "Payments.depositPolicy.needsPayments": "Activez d'abord les paiements en ligne : un acompte s'encaisse par carte, il ne peut pas fonctionner sans eux.",
    "Payments.depositPolicy.typeLabel": "Comment l'acompte se calcule",
    "Payments.depositPolicy.typePercentage": "Une part du total",
    "Payments.depositPolicy.typeFixed": "Un montant fixe",
    "Payments.depositPolicy.valuePercentage": "Part du total (%)",
    "Payments.depositPolicy.valueFixed": "Montant ($)",
    "Payments.depositPolicy.floorNote": "Sous {floor}, aucun acompte n'est demandé, et aucun ne l'est non plus quand le minimum dépasserait la moitié du prix de la réservation.",
    "Payments.depositPolicy.refundable": "Rendre l'acompte quand un client annule à temps",
    "Payments.depositPolicy.windowLabel": "Annuler au moins ce nombre d'heures à l'avance",
    "Payments.depositPolicy.lastminuteLabel": "Une réservation prise à l'intérieur de cette fenêtre",
    "Payments.depositPolicy.lastminuteNonRefundable": "Garder l'acompte",
    "Payments.depositPolicy.lastminuteRefundable": "Rendre l'acompte quand même",
    "Payments.depositPolicy.lastminuteNoDeposit": "Ne demander aucun acompte",
    "Payments.depositPolicy.save": "Enregistrer les réglages d'acompte",
    "Payments.depositPolicy.saved": "Réglages d'acompte enregistrés.",
    "Payments.depositPolicy.saveFailed": "Les réglages d'acompte n'ont pas pu être enregistrés.",
    "Payments.depositPolicy.ownerOnly": "Seul le propriétaire peut modifier les réglages d'acompte.",
    "Payments.depositPolicy.invalidValue": "Saisissez un montant supérieur à zéro.",
    "Payments.depositPolicy.percentageTooHigh": "Un pourcentage ne peut pas dépasser 100.",
    "Payments.depositPolicy.invalidWindow": "Saisissez un nombre entier d'heures, positif ou nul.",
    "Payments.depositPolicy.depositFloorRefusal": "Un acompte fixe ne peut pas être inférieur à {floor}.",
    "Payments.depositPolicy.windowRefusal": "La fenêtre d'annulation ne peut pas dépasser {max} heures.",
    "Payments.depositPolicy.invalidSetting": "Ce réglage n'est pas valide. Vérifiez les valeurs saisies.",

    // ── Mode « un montant pour tout le rendez-vous » et seuil réglable ──────
    "Payments.depositPolicy.typeFlat": "Un montant pour tout le rendez-vous",
    "Payments.depositPolicy.valueFlat": "Montant du rendez-vous ($)",
    "Payments.depositPolicy.minBasketLabel": "Seulement si le rendez-vous dépasse ($)",
    "Payments.depositPolicy.minBasketHelp": "Sous ce total, aucun acompte n'est demandé.",
    "Payments.depositPolicy.invalidMinBasket": "Saisissez un montant nul ou positif.",
    "Payments.depositPolicy.flatRatioWarning": "Avec ce seuil, un client dont le rendez-vous dépasse à peine ce montant paierait d'avance plus de la moitié du prix.",
    "Payments.depositPolicy.lastminuteFlaggedNote": "Cette dispense ne s'applique jamais à un client signalé.",

    // ── Carte « Clients signalés » ──────────────────────────────────────────
    "Payments.flaggedDeposit.title": "Clients signalés",
    "Payments.flaggedDeposit.body": "Demandez un acompte aux clients que vous avez signalés, même quand vous n'en demandez aucun aux autres.",
    "Payments.flaggedDeposit.enable": "Exiger un acompte des clients signalés",
    "Payments.flaggedDeposit.valueLabel": "Montant ($)",
    "Payments.flaggedDeposit.minBasketLabel": "À partir de ($)",
    "Payments.flaggedDeposit.discretionNote": "Le client ne voit aucune mention de son statut.",
    "Payments.flaggedDeposit.whereNote": "Vous réglez le statut dans la fiche client, onglet Profil, champ Type de client.",
    "Payments.flaggedDeposit.onlineOnlyNote": "Réservations en ligne seulement. Rien ne change au comptoir ni sur la ligne téléphonique.",
    "Payments.flaggedDeposit.needsPayments": "Activez d'abord les paiements en ligne : un acompte s'encaisse par carte, il ne peut pas fonctionner sans eux.",
    "Payments.flaggedDeposit.invalidValue": "Saisissez un montant supérieur à zéro.",
    "Payments.flaggedDeposit.invalidMinBasket": "Saisissez un montant nul ou positif.",
    "Payments.flaggedDeposit.save": "Enregistrer les réglages des clients signalés",
    "Payments.flaggedDeposit.saved": "Réglages des clients signalés enregistrés.",
    "Payments.flaggedDeposit.saveFailed": "Les réglages des clients signalés n'ont pas pu être enregistrés.",
    "Payments.flaggedDeposit.ownerOnly": "Seul le propriétaire peut modifier ces réglages.",

    "Payments.charges.title": "Accepter les paiements en ligne",
    "Payments.charges.onBody": "Vos clients peuvent régler leurs factures par carte. Retirez cette option pour cesser d'encaisser sans déconnecter votre compte Stripe.",
    "Payments.charges.offBody": "Les paiements en ligne sont suspendus. Votre compte Stripe reste connecté et vous gardez l'accès à l'argent déjà encaissé.",
    "Payments.charges.blockedByStripe": "Stripe n'autorise pas encore les encaissements sur votre compte. Terminez votre vérification pour que l'argent puisse entrer.",
    "Payments.charges.ownerOnly": "Seul le propriétaire du salon peut modifier ce réglage.",
    "Payments.charges.resumed": "Paiements en ligne repris.",
    "Payments.charges.pausedToast": "Paiements en ligne suspendus.",
    "Payments.charges.error": "Impossible d'enregistrer ce réglage.",
    "Payments.onboardingCta.title": "Terminez la connexion de votre compte Stripe",
    "Payments.onboardingCta.body": "Les acomptes et le paiement de factures en ligne restent inactifs tant que Stripe n'a pas confirmé que votre compte peut encaisser.",
    "Payments.onboardingCta.action": "Poursuivre la configuration",
    "Payments.transactions.title": "Transactions",
    "Payments.transactions.subtitle": "Tous les acomptes et toutes les factures payés en ligne, avec leur statut Stripe.",
    "Payments.transactions.empty": "Aucun paiement en ligne pour l'instant.",
    "Payments.transactions.loadError": "Impossible de charger les transactions.",
    "Payments.transactions.col.date": "Date",
    "Payments.transactions.col.kind": "Type",
    "Payments.transactions.col.amount": "Montant",
    "Payments.transactions.col.status": "Statut",
    "Payments.transactions.col.card": "Carte",
    "Payments.transactions.col.action": "Action",
    "Payments.transactions.kind.deposit": "Acompte",
    "Payments.transactions.kind.invoice": "Facture",
    "Payments.transactions.status.pending": "En attente",
    "Payments.transactions.status.paid": "Payé",
    "Payments.transactions.status.failed": "Échoué",
    "Payments.transactions.status.refund_pending": "Remboursement en attente",
    "Payments.transactions.status.partially_refunded": "Partiellement remboursé",
    "Payments.transactions.status.refunded": "Remboursé",
    "Payments.transactions.status.expired": "Expiré",
    "Payments.transactions.linkCreated": "Lien créé",
    "Payments.transactions.refundedAmount": "Remboursé :",
    "Payments.transactions.refundableRemaining": "Reste remboursable :",
    "Payments.transactions.refundSuccess": "Remboursement transmis à Stripe.",
    "Payments.transactions.refundUnconfirmed": "Ce remboursement n'est pas confirmé. Nous le vérifions auprès de Stripe : aucun autre remboursement ne partira sur cette transaction avant la fin de cette vérification, d'ici deux minutes.",
    "Payments.reauth.settings.title": "Confirmez votre identité",
    "Payments.reauth.settings.description": "Les réglages de paiement touchent à de l'argent réel. Confirmez que c'est bien vous avant de les modifier.",
    "Payments.reauth.refund.title": "Confirmez ce remboursement",
    "Payments.reauth.refund.description": "Un remboursement renvoie de l'argent à votre client. Confirmez que c'est bien vous.",

    // ── Refonte du 10-09 : onglets, pagination, recherche ───────────────────
    "Payments.tabs.collect": "À encaisser",
    "Payments.tabs.transactions": "Transactions",
    "Payments.tabs.deposits": "Acomptes par service",
    "Payments.list.searchPlaceholder": "Rechercher",
    "Payments.list.noResults": "Aucun résultat pour « {query} ».",
    "Payments.list.noResultsFiltered": "Aucun résultat pour ces filtres.",
    "Payments.collect.totalDue": "{amount} à recouvrer",
    "Payments.collect.countLine": "{count} factures impayées, dont {late} de plus de {critical} jours",
    "Payments.collect.countLineOne": "1 facture impayée, dont {late} de plus de {critical} jours",
    "Payments.collect.sort.label": "Trier",
    "Payments.collect.sort.oldest": "Les plus anciennes",
    "Payments.collect.sort.newest": "Les plus récentes",
    "Payments.collect.sort.amount": "Les plus gros montants",
    "Payments.collect.col.customer": "Client",
    "Payments.collect.col.invoice": "Facture",
    "Payments.collect.col.issued": "Émise le",
    "Payments.collect.col.age": "Âge",
    "Payments.collect.col.due": "Dû",
    "Payments.collect.col.action": "Action",
    "Payments.age.days": "{count} jours",
    "Payments.age.oneDay": "1 jour",
    "Payments.age.today": "Aujourd'hui",
    "Payments.age.unknown": "Inconnu",
    "Payments.transactions.totals.collected": "Encaissé",
    "Payments.transactions.totals.refunded": "Remboursé",
    "Payments.transactions.totals.net": "Net",
    "Payments.transactions.totals.succeeded": "{succeeded} transactions réussies sur {attempted} tentatives",
    "Payments.transactions.filter.status": "Statut",
    "Payments.transactions.filter.kind": "Type",
    "Payments.transactions.filter.all": "Tous",
    "Payments.transactions.menu.label": "Actions",
    "Payments.transactions.menu.refundBalance": "Rembourser le solde, {amount}",
    "Payments.transactions.menu.refundAmount": "Rembourser un montant",
    "Payments.refundDialog.title": "Rembourser un montant",
    "Payments.refundDialog.body": "Il reste {max} remboursables sur cette transaction.",
    "Payments.refundDialog.amountLabel": "Montant à rembourser",
    "Payments.refundDialog.confirm": "Rembourser",
    "Payments.refundDialog.cancel": "Annuler",
    "Payments.refundDialog.error.invalid_amount": "Saisissez un montant valide, supérieur à zéro, avec au plus deux décimales.",
    "Payments.refundDialog.error.exceeds_refundable": "Ce montant dépasse ce qui reste remboursable sur cette transaction.",
    "Payments.refundDialog.error.exceeds_invoice_collected": "Ce montant dépasse ce que cette facture tient encore : un avoir ou un autre remboursement a déjà rendu une partie de son argent.",
    "Payments.refundDialog.error.exceeds_invoice_collected_max": "Maximum possible : {max}.",
    "Payments.refundDialog.error.daily_cap_reached": "Votre salon a atteint sa limite de remboursement pour aujourd'hui. Réessayez demain.",
    "Payments.refundDialog.error.unknown": "Le remboursement n'a pas pu être effectué.",
    "Payments.refundDialog.error.nothing_refundable": "Il ne reste rien à rembourser sur cette transaction.",
    "Payments.refundDialog.error.not_found": "Cette transaction est introuvable. Actualisez la page.",
    "Payments.refundDialog.error.refund_in_progress": "Un remboursement est déjà en vérification sur cette transaction. Réessayez dans deux minutes.",
    "Payments.refundDialog.error.invoice_refund_in_progress": "Un remboursement est déjà en vérification sur un autre paiement de cette facture. Réessayez dans deux minutes.",
    "Payments.refundDialog.error.daily_cap_unreadable": "La limite de remboursement du jour n'a pas pu être vérifiée, donc rien n'a été remboursé. Réessayez dans un instant.",
    "Payments.refundDialog.error.transaction_unreadable": "Cette transaction n'a pas pu être lue, donc rien n'a été remboursé. Réessayez dans un instant.",
    "Payments.refundDialog.error.stripe_refused": "Stripe a refusé ce remboursement, et aucun argent n'a été déplacé. Vérifiez le montant, puis réessayez.",
    "Payments.refundDialog.error.deposit_not_refundable_here": "Un acompte ne se rembourse pas ici : il suit la politique d'annulation du rendez-vous.",
    "Payments.refundDialog.error.access_check_unavailable": "La vérification des droits est momentanément indisponible. Réessayez dans un instant.",
    "Payments.refundDialog.error.access_denied": "Vous n'avez pas la permission de rembourser ce paiement.",
    "Payments.refundDialog.error.reauth_required": "Pour votre sécurité, confirmez de nouveau votre identité, puis recommencez.",
    "Payments.deposits.col.service": "Prestation",
    "Payments.deposits.col.price": "Prix",
    "Payments.deposits.col.rule": "Règle",
    "Payments.deposits.col.deposit": "Acompte à la réservation",
    "Payments.deposits.ruleSalon": "Politique du salon",

    // ── Lot 2 : l'état du lien de paiement (C2) ─────────────────────────────
    "Payments.collect.col.link": "Lien de paiement",
    "Payments.collect.link.never": "Jamais envoyé",
    "Payments.collect.link.sent": "Envoyé le {date}",
    "Payments.collect.link.sentDetail": "par {channel}, expire le {date}",
    "Payments.collect.link.expiresOn": "expire le {date}",
    "Payments.collect.link.expired": "Expiré le {date}",
    "Payments.collect.link.expiredNoDate": "Expiré",
    "Payments.collect.link.revoked": "Annulé le {date}",
    "Payments.collect.link.revokedNoDate": "Annulé",
    "Payments.collect.link.channel.email": "courriel",
    "Payments.collect.link.channel.sms": "texto",
    "Payments.collect.link.channel.qr": "code QR",
    "Payments.collect.action.send": "Envoyer",
    "Payments.collect.action.remind": "Relancer",
    "Payments.collect.action.resend": "Renvoyer",

    // ── Lot 2 : la cliente et le rattachement (C1) ──────────────────────────
    "Payments.transactions.col.customer": "Client",
    "Payments.transactions.col.attached": "Rattaché à",
    "Payments.transactions.attachedInvoice": "Facture {number}",
    "Payments.transactions.attachedDeposit": "Acompte, RDV du {date}",

    // ── Lot 2 : l'export (C3) ───────────────────────────────────────────────
    "Payments.export.one": "Exporter {count} ligne",
    "Payments.export.many": "Exporter {count} lignes",
    "Payments.csv.ageDays": "Âge en jours",

    // ── Lot 2 : période, totaux filtrés (C4), tuile des totaux (m9) ─────────
    "Payments.transactions.totals.countLabel": "Transactions",
    "Payments.transactions.filter.period": "Période",
    "Payments.transactions.period.all": "Toute la période",
    "Payments.transactions.period.thisMonth": "Ce mois-ci",
    "Payments.transactions.period.lastMonth": "Le mois dernier",
    "Payments.transactions.period.last30": "Les 30 derniers jours",
    "Payments.transactions.period.thisYear": "Cette année",
    "Payments.list.forTheseFilters": "Chiffres pour ces filtres",

    // ── Lot 2 : le filtre d'âge (C4) ────────────────────────────────────────
    "Payments.collect.ageFilter.label": "Âge",
    "Payments.collect.ageFilter.all": "Tous les âges",
    "Payments.collect.ageFilter.under": "Moins de {max} jours",
    "Payments.collect.ageFilter.between": "{min} à {max} jours",
    "Payments.collect.ageFilter.atLeast": "{min} jours et plus",

    // ── Restes de l'audit multi-emplacements, problème 8 ────────────────────
    "Payments.brandWideSettingNote": "Ce réglage vaut pour tous vos emplacements.",
  },
};
